import React, { useState, useEffect } from 'react';
import { Star, Trash2, Plus, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = ({ onClose }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', company: '', rating: 5, comment: '', date: '' });
  const [adding, setAdding] = useState(false);

  const fetchReviews = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reviews`);
      const data = await res.json();
      setReviews(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchReviews(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      setForm({ name: '', company: '', rating: 5, comment: '', date: '' });
      await fetchReviews();
    } catch (e) { console.error(e); }
    setAdding(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    await fetch(`${API_URL}/api/reviews/${id}`, { method: 'DELETE' });
    await fetchReviews();
  };

  return (
    <div style={{ minHeight: '100vh', background: '#faf8f5', padding: '2rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
          <button 
            onClick={onClose} 
            data-testid="admin-back-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3a2e0f' }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#3a2e0f', margin: 0 }}>
            Administration des avis
          </h1>
        </div>

        {/* Add review form */}
        <div style={{
          background: 'white',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#3a2e0f', marginBottom: '1rem' }}>
            <Plus size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem' }} />
            Ajouter un avis
          </h2>
          <form onSubmit={handleAdd} style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <input
                required
                placeholder="Nom du client"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                data-testid="admin-review-name"
                style={inputStyle}
              />
              <input
                placeholder="Entreprise / Ville (optionnel)"
                value={form.company}
                onChange={e => setForm({ ...form, company: e.target.value })}
                data-testid="admin-review-company"
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>Note</label>
                <select
                  value={form.rating}
                  onChange={e => setForm({ ...form, rating: parseInt(e.target.value) })}
                  data-testid="admin-review-rating"
                  style={inputStyle}
                >
                  {[5, 4, 3, 2, 1].map(n => (
                    <option key={n} value={n}>{n} {'*'.repeat(n)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm({ ...form, date: e.target.value })}
                  data-testid="admin-review-date"
                  style={inputStyle}
                />
              </div>
            </div>
            <textarea
              required
              placeholder="Texte de l'avis..."
              value={form.comment}
              onChange={e => setForm({ ...form, comment: e.target.value })}
              rows={3}
              data-testid="admin-review-comment"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <button
              type="submit"
              disabled={adding}
              data-testid="admin-add-review-btn"
              style={{
                background: '#3a2e0f',
                color: 'white',
                border: 'none',
                padding: '0.65rem 1.5rem',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem'
              }}
            >
              {adding ? 'Ajout...' : 'Ajouter cet avis'}
            </button>
          </form>
        </div>

        {/* Reviews list */}
        <div style={{
          background: 'white',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#3a2e0f', marginBottom: '1rem' }}>
            Avis existants ({reviews.length})
          </h2>
          {loading ? (
            <p style={{ color: '#999' }}>Chargement...</p>
          ) : reviews.length === 0 ? (
            <p style={{ color: '#999' }}>Aucun avis pour le moment.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {reviews.map(r => (
                <div key={r.id} data-testid={`admin-review-${r.id}`} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: '#faf8f5',
                  borderRadius: '0.5rem',
                  border: '1px solid #eee'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <strong style={{ fontSize: '0.9rem', color: '#3a2e0f' }}>{r.name}</strong>
                      <span style={{ color: '#ac9969', fontSize: '0.85rem' }}>
                        {Array.from({ length: r.rating }, (_, i) => '★').join('')}
                      </span>
                      {r.date && <span style={{ fontSize: '0.75rem', color: '#999' }}>{r.date}</span>}
                    </div>
                    {r.company && <p style={{ fontSize: '0.8rem', color: '#888', margin: '0 0 0.25rem' }}>{r.company}</p>}
                    <p style={{ fontSize: '0.85rem', color: '#555', margin: 0 }}>{r.comment}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(r.id)}
                    data-testid={`admin-delete-${r.id}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#dc2626',
                      padding: '0.25rem',
                      marginLeft: '0.5rem',
                      flexShrink: 0
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%',
  padding: '0.55rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '0.5rem',
  fontSize: '0.9rem',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box'
};

export default Admin;
